import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useIntl } from "react-intl";
import PropTypes from 'prop-types';
import classnames from "classnames";

import styles from './ProgressBar.module.scss';
import messages from "./messages";
import { PROGRESS_ESTIMATE_BAR, PROGRESS_INNER_BAR } from "./constants";

const ProgressBar = ({
  hasEstimatedProgress = false,
  tiers,
}) => {
  const { promoBanner } = useSelector((state) => state.cart);
  const { formatMessage } = useIntl();

  const isSingleTier = tiers.length === 1;

  const innerRefs = useMemo(() => [], []);
  innerRefs[0] = useRef(null);
  innerRefs[1] = useRef(null);
  innerRefs[2] = useRef(null);
  innerRefs[3] = useRef(null);

  const estimateRefs = useMemo(() => [], []);
  estimateRefs[0] = useRef(null);
  estimateRefs[1] = useRef(null);
  estimateRefs[2] = useRef(null);
  estimateRefs[3] = useRef(null);

  const labelsParentRefs = useMemo(() => [], []);
  labelsParentRefs[0] = useRef(null);
  labelsParentRefs[1] = useRef(null);
  labelsParentRefs[2] = useRef(null);
  labelsParentRefs[3] = useRef(null);

  const setProgressStyling = useCallback((progressBarClassname) => {
    const isEstimate = progressBarClassname === PROGRESS_ESTIMATE_BAR;
    const estimatedSavings = promoBanner?.estimatedQualifiedSavings;
    const bmsmSavings = isEstimate
      ? estimatedSavings
      : promoBanner?.currentQualifiedSavings;

    Object.keys(bmsmSavings || {}).forEach((key, index) => {
      const targetElement = isEstimate ? estimateRefs[index].current : innerRefs[index].current;
      if (targetElement) {
        const savings = bmsmSavings[key];
        const isFull = savings >= 100;

        targetElement.style.border = isFull || savings > 0 ? '0' : '';
        targetElement.style.width = `${Math.min(Math.floor(savings), 100)}%`;


        const labelsParent = labelsParentRefs[index].current;
        if (isFull && !isEstimate && labelsParent) {
          labelsParent.style.opacity = 1;
        }
        if (!isFull && !isEstimate && labelsParent) {
          labelsParent.style.opacity = 0.6;
        }

        if (isFull) {
          targetElement.parentElement.style.borderRight = '1px solid #fff';
        } else if (index < 3) {
          targetElement.parentElement.style.borderRight = '';
        }
      }
    });
  }, [
    promoBanner?.currentQualifiedSavings,
    promoBanner?.estimatedQualifiedSavings,
    estimateRefs,
    innerRefs,
    labelsParentRefs
  ]);

  useEffect(() => {
    setProgressStyling(PROGRESS_INNER_BAR);
    if (hasEstimatedProgress) {
      setProgressStyling(PROGRESS_ESTIMATE_BAR);
    }
  }, [
    hasEstimatedProgress,
    setProgressStyling
  ]);

  return (
    <div className={classnames(styles['progress-bar-wrapper'], {
      [styles['is-single-tier']]: isSingleTier
    })}>
      <div className={styles['progress-bar']}>
        {tiers?.map((promoBannerTier, index) => (
          <div className={styles['outer-bar']} key={`promo-banner-bars-${promoBannerTier.tier}`}>
            <div data-testid={`inner-bar-${index}`} className={styles['promo-banner-inner-bar']} ref={innerRefs[index]} />
            {hasEstimatedProgress && (
              <div data-testid={`inner-estimate-bar-${index}`} className={styles['promo-banner-inner-estimate-bar']} ref={estimateRefs[index]} />
            )}
            <div className={classnames(styles['background-bar'], 'background-bar')} />
          </div>
        ))}
      </div>
      <div className={styles['progress-bar-labels']}>
        {tiers?.map((promoBannerTier, index) => (
          <div data-testid={`promo-banner-labels-${index}`} className={styles['label-wrapper']} key={`promo-banner-labels-${promoBannerTier.tier}`} ref={labelsParentRefs[index]}>
            {!isSingleTier && (
              <div className={styles['savings-label']}>{formatMessage(messages.dollarOffMessaging, {
                dollarOffAmount: promoBannerTier?.discount
              })}</div>
            )}
            <div className={styles['min-amount-label']}>${promoBannerTier?.tier}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

ProgressBar.propTypes = {
  hasEstimatedProgress: PropTypes.bool,
  tiers: PropTypes.arrayOf(PropTypes.shape({
    discount: PropTypes.string,
    tier: PropTypes.string,
  })).isRequired,
};

export default ProgressBar;